/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Pressable, View, Switch } from '@components/atoms'
import { ForgotPasswordMessage } from '@components/molecules'
import { BackofficeTheme } from '@components/systems/backoffice/BackofficeTheme'
import {
	mono,
	boFont,
	boShared,
	boSize,
} from '@components/systems/backoffice/backoffice.tokens'
// 원형 사자 + Judahs 결합 로고 — 텍스트만 로고로 쓰지 않는다 (2026-09-28 사용자 결정)
import logoTitlePng from '@public/images/logo/logo_title.png'

import packageJson from '../../../../package.json'
import { produce } from 'immer'
import {
	useAppSelector,
	useAppDispatch,
	authActions,
	configActions,
	AuthStateType,
} from '@redux/index'
import { useNavigate } from 'react-router-dom'

/**
 * 백오피스 로그인 — Monolith 콜드 라이트.
 * 좌: Thin 대형 카피(기존 문구 유지) · 우: 밑줄 입력 두 줄 + 블랙 CTA 하나.
 * 사진·라운드 카드·SimpleNav 는 걷어냈다. 동작(로그인·아이디 저장·패스워드 안내)은 그대로.
 */
const LoginPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// variables
	const localUserID = localStorage.getItem('userAccount') ?? ''
	const defaultRememberIdYn: boolean = React.useMemo(() => {
		if (localUserID === '') {
			return false
		} else {
			return true
		}
	}, [])
	const defualtAccount: AuthStateType['login'] = defaultRememberIdYn
		? { email: localUserID, password: '' }
		: { email: '', password: '' }

	// redux state
	const systemName: string = useAppSelector(state => state.config.systemName)
	const systemSubtitle: string = useAppSelector(state => state.config.systemSubtitle)
	const isLoading: boolean = useAppSelector(state => state.config.isLoading as boolean)
	const isLogin: boolean = useAppSelector(state => state.auth.isLogin as boolean)
	const loginErrorMessage: string = useAppSelector(state => state.auth.loginErrorMessage)
	const isRememberId: boolean = useAppSelector(state => state.auth.isRememberId)

	// state
	const [account, setAccount] = React.useState(defualtAccount)
	const [showForgotPassword, setShowForgotPassword] = React.useState<boolean>(false)

	React.useEffect(() => {
		reduxDispatch(authActions.IS_REMEMBER_ID(defaultRememberIdYn))
	}, [defaultRememberIdYn])

	React.useEffect(() => {
		if (isLoading) {
			const timeout = setTimeout(() => {
				reduxDispatch(configActions.IS_LOADING(false))
				clearTimeout(timeout)
			}, 2000)
		}
	}, [isLoading])

	React.useEffect(() => {
		if (isLogin) {
			navigate('/auth/loading')
		}
	}, [isLogin])

	const onIdChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value
			const newAccount = produce(account, draft => {
				draft.email = str
			})

			setAccount(newAccount)
		},
		[account],
	)

	const onPwChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value
			const newAccount = produce(account, draft => {
				draft.password = str
			})

			setAccount(newAccount)
		},
		[account],
	)

	const onInputEnterSubmit = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				onLoginClick()
			}
		},
		[account],
	)

	const onLoginClick = React.useCallback(() => {
		if (account.email !== '' && account.password !== '') {
			reduxDispatch(
				authActions.LOGIN({ email: account.email, password: account.password }),
			)
		} else {
			reduxDispatch(authActions.LOGIN_ERROR_MESSAGE('로그인 정보를 다시 확인해주세요.'))
		}
	}, [account])

	const onForgotPasswordClick = React.useCallback(() => {
		setShowForgotPassword(!showForgotPassword)
	}, [showForgotPassword])

	const onRememberIdChange = () => {
		reduxDispatch(authActions.IS_REMEMBER_ID(!isRememberId))
	}

	return (
		<BackofficeTheme>
			<View css={style}>
				<View className="topBar">
					<View className="brand">
						<img src={logoTitlePng} alt={systemName} className="brandLogo" />
					</View>
					<span className="boMeta">Backoffice</span>
				</View>

				{showForgotPassword ? (
					<ForgotPasswordMessage onBackClick={onForgotPasswordClick} />
				) : (
					<View className="body">
						{/* 회사 정체성 — 홈 About 의 세 가치(뒤에 선다 · 조용하다 · 방 크기로 기준을 나누지 않는다)를 같은 리듬으로 (PERSONA §1) */}
						<View className="slogan">
							<p className="sloganLine">Behind the player.</p>
							<p className="sloganLine">Quiet by design.</p>
							<p className="sloganLine">One unit, every room.</p>
							<p className="sloganLine dim">Applause is not ours.</p>
							<a
								className="boMeta sloganRef"
								href="/about"
								target="_blank"
								rel="noreferrer"
							>
								About Judahs
							</a>
						</View>

						<View className="panel">
							<span className="boKicker">Sign in</span>
							<h1 className="panelTitle">
								<img src={logoTitlePng} alt={systemName} className="panelLogo" />
							</h1>
							{systemSubtitle !== '' ? (
								<p className="boLead panelLead">{systemSubtitle}</p>
							) : null}

							<View className="boField field">
								<label className="boLabel" htmlFor="loginEmail">
									이메일
								</label>
								<input
									id="loginEmail"
									className="boInput"
									type="text"
									autoComplete="username"
									defaultValue={account.email}
									onChange={onIdChange}
									onKeyUp={onInputEnterSubmit}
								/>
							</View>

							<View className="boField field">
								<label className="boLabel" htmlFor="loginPassword">
									패스워드
								</label>
								<input
									id="loginPassword"
									className={`boInput ${loginErrorMessage !== '' ? 'error' : ''}`}
									type="password"
									autoComplete="current-password"
									onChange={onPwChange}
									onKeyUp={onInputEnterSubmit}
								/>
								{loginErrorMessage !== '' ? (
									<span className="boError">{loginErrorMessage}</span>
								) : null}
							</View>

							<label className="rememberRow">
								<Switch
									size="small"
									checked={isRememberId}
									onChange={onRememberIdChange}
									inputProps={{ 'aria-label': '아이디 저장' }}
								/>
								<span className="rememberLabel">아이디 저장</span>
							</label>

							<button type="button" className="boBtn loginButton" onClick={onLoginClick}>
								로그인
							</button>

							<Pressable className="boLink forgotLink" onClick={onForgotPasswordClick}>
								패스워드를 잊어버렸다
							</Pressable>
						</View>
					</View>
				)}

				<span className="version">{`v ${packageJson.version}`}</span>
			</View>
		</BackofficeTheme>
	)
}

export default LoginPage

const style = css({
	...boShared,
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	minHeight: ['100vh', '100dvh'],
	background: mono.bg,
	fontFamily: boFont.ui,

	'& .text': { fontFamily: boFont.ui },

	'.topBar': {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: boSize.navH + 24,
		padding: `0 ${boSize.gutter}`,

		'.brand': { display: 'flex', alignItems: 'center', gap: 12 },
		'.brandLogo': { height: 26, width: 'auto', display: 'block' },
	},

	'.body': {
		flex: 1,
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
		gap: 'clamp(32px, 6vw, 96px)',
		alignItems: 'center',
		padding: `clamp(32px, 6vh, 80px) ${boSize.gutter} 96px`,
		maxWidth: 1440,
		width: '100%',
		margin: '0 auto',
		boxSizing: 'border-box',
	},

	'.slogan': {
		'.sloganLine': {
			fontSize: 'clamp(40px, 5.2vw, 84px)',
			fontWeight: 100,
			lineHeight: 1.02,
			letterSpacing: '-0.015em',
			color: mono.ink,
			'&.dim': { color: mono.faint, marginTop: '0.3em' },
		},
		'.sloganRef': {
			display: 'inline-block',
			marginTop: 28,
			textDecoration: 'none',
			'&:hover': { color: mono.ink },
		},
	},

	'.panel': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		maxWidth: 400,
		marginLeft: 'auto',

		'.panelTitle': {
			marginTop: 12,
			lineHeight: 0,
			'.panelLogo': { height: 'clamp(34px, 3vw, 42px)', width: 'auto', display: 'block' },
		},
		'.panelLead': { marginTop: 8 },

		'.field': { marginTop: 28 },

		'.rememberRow': {
			display: 'flex',
			alignItems: 'center',
			gap: 4,
			marginTop: 20,
			marginLeft: -8,
			cursor: 'pointer',
			'.rememberLabel': { fontSize: 13, fontWeight: 300, color: mono.dim },
		},

		'.loginButton': { width: '100%', height: 48, marginTop: 28, fontSize: 14 },

		'.forgotLink': {
			alignSelf: 'flex-start',
			marginTop: 20,
			fontSize: 13,
			fontWeight: 400,
			color: mono.dim,
			borderBottomColor: mono.lineSoft,
			'&:hover': { color: mono.ink, borderBottomColor: mono.ink },
		},
	},

	'.version': {
		position: 'absolute',
		right: boSize.gutter,
		bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
		fontSize: 11,
		fontWeight: 300,
		color: mono.faint,
		fontVariantNumeric: 'tabular-nums',
	},

	'@media (max-width: 900px)': {
		'.body': {
			gridTemplateColumns: '1fr',
			gap: 48,
			alignItems: 'start',
			paddingTop: 24,
		},
		'.slogan .sloganLine': { fontSize: 'clamp(32px, 9vw, 56px)' },
		'.panel': { marginLeft: 0, maxWidth: '100%' },
	},
})
